import {
  CaseReducer as CR,
  Dictionary,
  EntityId,
  PayloadAction as PA,
} from '@reduxjs/toolkit';
import { CUSTOM_USERS } from 'components/twilio/constants';
import uniq from 'lodash/uniq';
import isEmpty from 'lodash/isEmpty';
import last from 'lodash/last';
import { toTwilioMessage } from './converters';
import {
  ConversationsClient,
  ConnectionState,
  userAdapter,
  TwilioUser,
  metadataAdapter,
  ConversationMetadata,
  Message,
  messageAdapter,
  ChatAlert,
  GroupConversationMetadata,
  TwilioSliceType as State,
  TwilioMessage,
  MessageType,
  PublicConversationMetadata,
} from './types';
import { determineReachabilityStatus } from './utils';

interface AddMessagePayload {
  conversationId: string;
  messages: Message[],
  updateLastMessage?: boolean;
  fromSelf?: boolean;
}

interface EditGroupParticipantsPayload {
  type: 'ADD' | 'REMOVE';
  conversationId: string;
  participantIds: string[];
}

interface UpdateReachabilityStatusPayload {
  id: string;
  isOnline: boolean;
  isIdle: boolean;
}

const setConversationsClient: CR<State, PA<ConversationsClient>> = (state, action) => {
  state.client = action.payload;
};

const setConnectionState: CR<State, PA<ConnectionState>> = (state, action) => {
  state.connectionState = action.payload;
};

const addParticipant: CR<State, PA<TwilioUser>> = (state, action) => {
  userAdapter.upsertOne(state.users, action.payload);
};

const updateParticipant: CR<State, PA<Partial<TwilioUser> & { id: string }>> = (state, action) => {
  const { id, ...updates } = action.payload;
  userAdapter.updateOne(
    state.users,
    {
      id: action.payload.id,
      changes: updates,
    },
  );
};

const updateReachabilityStatus: CR<State, PA<UpdateReachabilityStatusPayload>> = (state, action) => {
  const { id, isOnline, isIdle } = action.payload;
  const reachabilityStatus = determineReachabilityStatus(isOnline, isIdle);
  userAdapter.updateOne(
    state.users,
    {
      id,
      changes: { reachabilityStatus },
    },
  );
};

const setChatInterface: CR<State, PA<boolean>> = (state, action) => {
  state.chatInterfaceActive = action.payload;
};

const addMetadata: CR<State, PA<ConversationMetadata>> = (state, action) => {
  metadataAdapter.addOne(state.metadata, action.payload);
  state.messages[action.payload.id] = messageAdapter.getInitialState();
};

const removeMetadata: CR<State, PA<EntityId>> = (state, action) => {
  const selectedConv = state.selectedChat;
  if (selectedConv === action.payload) {
    state.selectedChat = null;
  }
  metadataAdapter.removeOne(state.metadata, action.payload);
  delete state.messages[action.payload];
};

const updateMetadata: CR<State, PA<Partial<ConversationMetadata> & { id: string }>> = (state, action) => {
  metadataAdapter.updateOne(
    state.metadata,
    {
      id: action.payload.id,
      changes: action.payload,
    },
  );
};

const setSelectedChat: CR<State, PA<string | null>> = (state, action) => {
  state.selectedChat = action.payload;
};

const parseIdFromMessage = (message: TwilioMessage): string => {
  switch (message.attributes.messageType) {
    case MessageType.ParticipantJoined:
    case MessageType.ParticipantLeft:
      return message.attributes.actionUuid;
    default: return message.author;
  }
};

const getUsers = (
  client: ConversationsClient,
  userEntity: Dictionary<TwilioUser>,
  ids: string[],
) => ids.forEach((id) => !userEntity[id] && client.getUser(id));

const addMessages: CR<State, PA<AddMessagePayload>> = (state, action) => {
  const {
    conversationId: id,
    messages,
    updateLastMessage = false,
    fromSelf = false,
  } = action.payload;
  const twilioMessages = messages.map((message) => toTwilioMessage(message));
  const ids = uniq(twilioMessages.map((message) => parseIdFromMessage((message))).filter((userId) => !CUSTOM_USERS.includes(userId)));
  getUsers(state.client as ConversationsClient, state.users.entities, ids);

  messageAdapter.upsertMany(state.messages[id], twilioMessages);
  if (!messages.length) return;
  const changes = {} as Partial<ConversationMetadata>;
  const messageIndex = messages[messages.length - 1].index;
  if (updateLastMessage) changes.lastMessage = last(twilioMessages);
  if (fromSelf) changes.lastReadMessageIndex = messageIndex;
  if (!isEmpty(changes)) metadataAdapter.updateOne(state.metadata, { id, changes });
};

const setChatAlert: CR<State, PA<ChatAlert>> = (state, action) => {
  state.alert = action.payload;
};

const editGroupParticipants: CR<State, PA<EditGroupParticipantsPayload>> = (state, action) => {
  const { type, participantIds, conversationId } = action.payload;
  const currentMetadata = state.metadata.entities[conversationId] as GroupConversationMetadata | PublicConversationMetadata;
  const changes = {
    otherUserIds: type === 'ADD'
      ? uniq([...currentMetadata.otherUserIds, ...participantIds])
      : currentMetadata.otherUserIds.filter((userId) => !participantIds.includes(userId)),
  } as Partial<GroupConversationMetadata | PublicConversationMetadata>;
  metadataAdapter.updateOne(state.metadata, { id: conversationId, changes });
};

const setImageViewerInitial: CR<State, PA<string>> = (state, action) => {
  state.imageViewerInitial = action.payload;
};

const setAllConversationsLoaded: CR<State, PA<boolean>> = (state, action) => {
  state.allConversationsLoaded = action.payload;
};

export default {
  setConversationsClient,
  setConnectionState,
  addParticipant,
  updateParticipant,
  setChatInterface,
  addMetadata,
  removeMetadata,
  updateMetadata,
  setSelectedChat,
  addMessages,
  setChatAlert,
  editGroupParticipants,
  setImageViewerInitial,
  setAllConversationsLoaded,
  updateReachabilityStatus,
};
